import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useTabelaHorariosOutletContext } from 'pages/TabelaHorario.context'
import { useMemo } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
    TabelaHorarioFuncionario,
    UpdateTabelaHorariosSchema,
    funcionario,
    tabelaHorarios,
    tempoParaMinutos,
    toleranciasEmMinutos,
    trocaHorario,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'
import { CanSubmitProvider, useCanSubmit } from '../context/CanSubmitContext'
import FormAssociarFuncionarios from '../forms/FormAssociarFuncionarios'
import FormTabelaHorario from '../forms/FormTabelaHorario'
import {
    AssociarFuncionarios,
    AssociarFuncionariosSchema,
} from '../schemas/associar-funcionarios.schema'

type FormValues = z.infer<typeof UpdateTabelaHorariosSchema> &
    AssociarFuncionarios

function EditarTabelaHorariosContent() {
    const { id } = useParams()
    const { data } = useTabelaHorariosOutletContext()
    const tabela = data.find(item => item.id === Number(id))
    const { canSubmit } = useCanSubmit()
    const canAssociarFuncionarios = hasPermission([
        'create',
        'TrocaDeTabelaDeHorário',
    ])
    const empresaId = useStore(state => state.companyId)
    const { mutateAsync: updateTabela, isLoading: updatingTabela } =
        tabelaHorarios.mutations.useUpdate({ invalidateQuery: true })
    const { mutateAsync: createTroca, isLoading: creatingTroca } =
        trocaHorario.mutations.useCreate({ invalidateQuery: true })
    const { data: vinculados = [] } = tabelaHorarios.useFetchByIdFrom<
        TabelaHorarioFuncionario[]
    >(tabela?.id ?? 0, 'funcionarios', {
        enabled: canAssociarFuncionarios && !!tabela,
    })
    const funcionariosVinculados = useMemo(
        () => vinculados.map(vinculo => vinculo.funcionarioId),
        [vinculados],
    )

    const tabs = useMemo(
        () => [
            {
                name: 'tabela',
                label: 'Tabela de horários',
                content: <FormTabelaHorario />,
            },
            ...(canAssociarFuncionarios
                ? [
                      {
                          name: 'funcionarios',
                          label: 'Funcionários',
                          content: (
                              <FormAssociarFuncionarios
                                  checked={funcionariosVinculados}
                              />
                          ),
                      },
                  ]
                : []),
        ],
        [tabela, canAssociarFuncionarios, funcionariosVinculados],
    )

    if (!tabela) return null

    const handleSubmit: SubmitHandler<FormValues> = async data => {
        const { funcionarios, ...tabelaData } = data

        try {
            await updateTabela(tabelaData)
        } catch (err: any) {
            toast.error(err.message)
            throw err
        }

        if (!funcionarios?.funcionarioIds?.length) {
            toast.success('Tabela atualizada com sucesso')
            return
        }

        try {
            await createTroca({
                empresaId,
                tabelaHorarioId: tabela.id,
                funcionarioIds: funcionarios.funcionarioIds,
                dtInicial: funcionarios.dtInicial,
                dtFinal: funcionarios.dtFinal,
            } as any)
            await funcionario.invalidateQueries()
            toast.success(
                'Tabela atualizada e funcionários vinculados com sucesso',
            )
        } catch (err: any) {
            toast.error(
                `Tabela salva, mas houve erro ao vincular os funcionários: ${err.message}`,
            )
        }
    }

    return (
        <TabbedDialog.Root
            width="5xl"
            loading={updatingTabela || creatingTroca}
            onSubmit={handleSubmit}
            defaultValues={{
                ...tabela,
                toleranciaSemana: tempoParaMinutos(tabela.toleranciaSemana),
                tolerancias: toleranciasEmMinutos(tabela.tolerancias),
                funcionarios: { funcionarioIds: [] },
            }}
            schema={z.intersection(
                UpdateTabelaHorariosSchema,
                AssociarFuncionariosSchema,
            )}
            open={hasPermission(['update', 'TabelaDeHorários'])}
            tabs={tabs}
        >
            <TabbedDialog.Title>Editar tabela de horários</TabbedDialog.Title>
            <TabbedDialog.Content />
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
                <TabbedDialog.Actions.PrimaryButton
                    closeOnSubmit
                    disabled={!canSubmit}
                />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}

export default function EditarTabelaHorarios() {
    return (
        <CanSubmitProvider>
            <EditarTabelaHorariosContent />
        </CanSubmitProvider>
    )
}
